<template>
    <BaseDropdownMenu :sections="menuSections" align="start" side="top" :side-offset="8" :aria-label="t('navigation.account.label')" @select="handleSelect">
        <template #trigger>
            <BaseButton
                data-testid="settings-button"
                type="button"
                theme="tertiary"
                size="sm"
                :icon-only="!showLabel"
                :aria-label="t('navigation.account.withOrganization', { organization: organizationName })"
                class="flex items-center gap-3 min-w-0"
                :class="{ 'self-center mx-auto': !showLabel, 'w-full': showLabel }">
                <img :src="logoUrl" alt="" aria-hidden="true" class="size-5 shrink-0 rounded-md object-contain" />
                <template v-if="showLabel">
                    <span class="flex-1 min-w-0 truncate text-left body-small-semibold text-ink">{{ organizationName }}</span>
                    <i class="bi bi-chevron-expand nav-icon shrink-0 text-nav-section-title" aria-hidden="true"></i>
                </template>
            </BaseButton>
        </template>
    </BaseDropdownMenu>
    <form ref="logoutForm" method="POST" :action="logoutUrl" style="display: none">
        <input type="hidden" name="csrfmiddlewaretoken" :value="getCookie('csrftoken')" />
    </form>
</template>

<script setup lang="ts">
import { getCookie } from "/js/utils.js";
import Logo from "~img/logo.png";
import type { GlobalProperties, OrganizationDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import { isInIFrame } from "~vue/utils";
import { discardBrowserHistory } from "~vue/utils/browserHistory";
import { shutdownIntercomMessengerEverywhere } from "~vue/utils/intercom";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, inject, type Ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const { showLabel = true } = defineProps<{ showLabel?: boolean }>();

const globalProperties = inject<GlobalProperties>("globalProperties");
const organizationDetails = inject<Ref<OrganizationDetails | null>>("organization_details");

const organizationName = computed(() => organizationDetails?.value?.name || "Valence");
const logoUrl = computed(() => organizationDetails?.value?.logo_square_url || organizationDetails?.value?.logo_url || Logo);

const feedbackUrl = isInIFrame() ? "/microsoft-teams/report-issue/" : (globalProperties?.$feedbackMailto ?? "");
const logoutUrl = isInIFrame() ? "/accounts/logout/?next=/microsoft-teams/login/" : "/accounts/logout/";

const menuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "account",
        items: [
            { id: "preferences", type: "inertia-link", href: "/accounts/user-preferences", label: t("navigation.account.preferences"), testId: "preferences" },
            { id: "feedback", type: "anchor", href: feedbackUrl, label: t("navigation.account.reportIssue"), testId: "feedback" },
        ],
    },
    {
        id: "session",
        items: [{ id: "logout", type: "button", label: t("navigation.account.logOut"), testId: "logout" }],
    },
]);

const logoutForm = useTemplateRef<HTMLFormElement>("logoutForm");

const handleSelect = (item: DropdownMenuItem) => {
    if (item.id === "feedback") {
        logUserInteraction("navigation_report_issue_clicked");
    }
    if (item.id === "logout") {
        discardBrowserHistory();
        shutdownIntercomMessengerEverywhere();
        logoutForm.value?.submit();
    }
};
</script>
